import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export interface BarChartSeries<T> {
  dataKey: keyof T & string;
  label: string;
  color: string;
  stackId?: string;
}

export interface BarChartWidgetProps<T extends Record<string, unknown>> {
  data: T[];
  xKey: keyof T & string;
  series: BarChartSeries<T>[];
  layout?: "vertical" | "horizontal";
  xFormatter?: (value: string) => string;
  yFormatter?: (value: number) => string;
  showGrid?: boolean;
  showLegend?: boolean;
  barSize?: number;
  height?: number;
  radius?: number;
}

function CustomTooltip({ active, payload, label, xFormatter, yFormatter }: any) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2">
      <p className="text-sm font-medium text-gray-700 mb-1">
        {xFormatter ? xFormatter(label) : label}
      </p>
      {payload.map((entry: any) => (
        <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-gray-600">{entry.name}:</span>
          <span className="font-medium text-gray-900">
            {yFormatter ? yFormatter(entry.value) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export function BarChartWidget<T extends Record<string, unknown>>({
  data,
  xKey,
  series,
  layout = "vertical",
  xFormatter,
  yFormatter,
  showGrid = true,
  showLegend = false,
  barSize,
  height = 280,
  radius = 4,
}: BarChartWidgetProps<T>) {
  if (!data.length) {
    return (
      <div
        className="flex items-center justify-center text-gray-400 text-sm"
        style={{ height }}
      >
        Inte tillräckligt med data
      </div>
    );
  }

  const isHorizontal = layout === "horizontal";

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout={isHorizontal ? "vertical" : "horizontal"}
        margin={{
          top: 5,
          right: 10,
          left: isHorizontal ? 80 : -10,
          bottom: 0,
        }}
      >
        {showGrid && (
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        )}
        {isHorizontal ? (
          <>
            <XAxis
              type="number"
              tickFormatter={yFormatter}
              tick={{ fontSize: 12, fill: "#9ca3af" }}
              axisLine={{ stroke: "#e5e7eb" }}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey={xKey}
              tickFormatter={xFormatter}
              tick={{ fontSize: 12, fill: "#6b7280" }}
              axisLine={false}
              tickLine={false}
              width={75}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey={xKey}
              tickFormatter={xFormatter}
              tick={{ fontSize: 12, fill: "#9ca3af" }}
              axisLine={{ stroke: "#e5e7eb" }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={yFormatter}
              tick={{ fontSize: 12, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
            />
          </>
        )}
        <Tooltip
          content={
            <CustomTooltip xFormatter={xFormatter} yFormatter={yFormatter} />
          }
        />
        {showLegend && (
          <Legend
            wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
            iconType="circle"
            iconSize={8}
          />
        )}
        {series.map((s) => (
          <Bar
            key={s.dataKey}
            dataKey={s.dataKey}
            name={s.label}
            fill={s.color}
            stackId={s.stackId}
            barSize={barSize}
            radius={[radius, radius, radius, radius]}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
